import { createSlice } from "@reduxjs/toolkit";
import {
  fetchStemProjects,
  fetchStemProjectById,
  fetchProjectStages,
  fetchProjectProgress,
  saveStageProgressThunk,
} from "./stemThunk";

const initialState = {
  projects: [],
  selectedProject: null,
  status: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,

  // Stages state for the selected project
  stages: [],
  stagesLoading: false,
  stagesError: null,

  // Completed stage IDs for active project
  completedStageIds: [],
  progressLoading: false,
  progressError: null,
};

export const stemSlice = createSlice({
  name: "stem",
  initialState,
  reducers: {
    clearStemError: (state) => {
      state.error = null;
    },
    setSelectedStemProject: (state, action) => {
      state.selectedProject = action.payload;
    },
    clearProjectStages: (state) => {
      state.stages = [];
      state.stagesLoading = false;
      state.stagesError = null;
      state.completedStageIds = [];
    },
    markStageCompleted: (state, action) => {
      const stageId = action.payload;
      if (stageId && !state.completedStageIds.includes(stageId)) {
        state.completedStageIds.push(stageId);
      }
    },
    setCompletedStageIds: (state, action) => {
      state.completedStageIds = action.payload || [];
    },
  },
  extraReducers: (builder) => {
    // fetchStemProjects (category 1)
    builder
      .addCase(fetchStemProjects.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(fetchStemProjects.fulfilled, (state, action) => {
        state.status = "succeeded";
        state.projects = action.payload;
      })
      .addCase(fetchStemProjects.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch STEM projects";
      });

    // fetchStemProjectById
    builder
      .addCase(fetchStemProjectById.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchStemProjectById.fulfilled, (state, action) => {
        state.selectedProject = action.payload;
      })
      .addCase(fetchStemProjectById.rejected, (state, action) => {
        state.error = action.payload || "Failed to fetch STEM project";
      });

    // fetchProjectStages (by projectId)
    builder
      .addCase(fetchProjectStages.pending, (state) => {
        state.stagesLoading = true;
        state.stagesError = null;
      })
      .addCase(fetchProjectStages.fulfilled, (state, action) => {
        state.stagesLoading = false;
        state.stages = action.payload || [];
      })
      .addCase(fetchProjectStages.rejected, (state, action) => {
        state.stagesLoading = false;
        state.stagesError = action.payload || "Failed to fetch project stages";
      });

    // fetchProjectProgress
    builder
      .addCase(fetchProjectProgress.pending, (state) => {
        state.progressLoading = true;
        state.progressError = null;
        state.completedStageIds = [];
      })
      .addCase(fetchProjectProgress.fulfilled, (state, action) => {
        state.progressLoading = false;
        state.completedStageIds = action.payload?.completedStageIds || [];
      })
      .addCase(fetchProjectProgress.rejected, (state, action) => {
        state.progressLoading = false;
        state.progressError = action.payload || "Failed to fetch project progress";
        state.completedStageIds = [];
      });

    // saveStageProgressThunk
    builder
      .addCase(saveStageProgressThunk.fulfilled, (state, action) => {
        const { stageId } = action.payload;
        if (stageId && !state.completedStageIds.includes(stageId)) {
          state.completedStageIds.push(stageId);
        }
      });
  },
});

export const {
  clearStemError,
  setSelectedStemProject,
  clearProjectStages,
  markStageCompleted,
  setCompletedStageIds,
} = stemSlice.actions;

export default stemSlice.reducer;
